import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  GameState,
  UserProgress,
  UserProfile,
  GameSession,
  VocabularyWord,
  GameType,
  DifficultyLevel,
  Language,
} from '../types';
import { storageService } from '../services/storageService';

interface AppContextType {
  // User data
  userProfile: UserProfile | null;
  userProgress: UserProgress | null;

  // Game state
  gameState: GameState;

  // Actions
  setUserProfile: (profile: UserProfile) => void;
  updateProgress: (updates: Partial<UserProgress>) => void;
  startGame: (gameType: GameType, level: DifficultyLevel, language: Language) => void;
  endGame: () => void;
  setCurrentWord: (word: VocabularyWord | null) => void;
  addScore: (points: number) => void;
  markWordLearned: (wordId: string) => void;
  showCelebration: () => void;
  hideCelebration: () => void;
  updateLevelProgress: (level: DifficultyLevel, completionPercentage: number, stars: number) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [userProfile, setUserProfileState] = useState<UserProfile | null>(null);
  const [userProgress, setUserProgressState] = useState<UserProgress | null>(null);
  const [gameState, setGameState] = useState<GameState>({
    currentSession: null,
    currentWord: null,
    isPlaying: false,
    isPaused: false,
    showCelebration: false,
  });

  // Load user data on mount
  useEffect(() => {
    const profile = storageService.getUserProfile();
    let progress = storageService.getUserProgress();

    setUserProfileState(profile);

    if (!progress) {
      // Initialize default progress if none exists
      const defaultProgress = storageService.initializeProgress('default-user');
      setUserProgressState(defaultProgress);
    } else {
      // Migrate old data to include new fields
      const today = new Date().toISOString().split('T')[0];
      const needsMigration =
        progress.currentStreak === undefined ||
        progress.dailyGoal === undefined ||
        progress.weeklyGames === undefined;

      if (needsMigration) {
        progress = storageService.updateProgress({
          currentStreak: progress.currentStreak ?? 0,
          longestStreak: progress.longestStreak ?? 0,
          lastStreakDate: progress.lastStreakDate ?? today,
          dailyGoal: progress.dailyGoal ?? 10,
          dailyProgress: progress.dailyProgress ?? 0,
          lastDailyReset: progress.lastDailyReset ?? today,
          weeklyGames: progress.weeklyGames ?? [0, 0, 0, 0, 0, 0, 0],
          weeklyScores: progress.weeklyScores ?? [0, 0, 0, 0, 0, 0, 0],
        });
      }

      // Check daily reset and streak
      progress = storageService.checkDailyReset();
      progress = storageService.updateStreak();

      setUserProgressState(progress);
    }
  }, []);

  // Actions
  const setUserProfile = (profile: UserProfile) => {
    storageService.saveUserProfile(profile);
    setUserProfileState(profile);
  };

  const updateProgress = (updates: Partial<UserProgress>) => {
    const updated = storageService.updateProgress(updates);
    setUserProgressState(updated);
  };

  const startGame = (gameType: GameType, level: DifficultyLevel, language: Language) => {
    const session: GameSession = {
      gameType,
      level,
      language,
      wordsCompleted: 0,
      correctAnswers: 0,
      score: 0,
      startTime: Date.now(),
    };

    setGameState({
      currentSession: session,
      currentWord: null,
      isPlaying: true,
      isPaused: false,
      showCelebration: false,
    });

    // Increment games played counter
    storageService.incrementGamesPlayed();
  };

  const endGame = () => {
    setGameState({
      currentSession: null,
      currentWord: null,
      isPlaying: false,
      isPaused: false,
      showCelebration: false,
    });
  };

  const setCurrentWord = (word: VocabularyWord | null) => {
    setGameState((prev) => ({
      ...prev,
      currentWord: word,
    }));
  };

  const addScore = (points: number) => {
    const updated = storageService.addScore(points);
    setUserProgressState(updated);

    // Update current session score
    setGameState((prev) => ({
      ...prev,
      currentSession: prev.currentSession
        ? {
            ...prev.currentSession,
            score: prev.currentSession.score + points,
          }
        : null,
    }));
  };

  const markWordLearned = (wordId: string) => {
    let updated = storageService.markWordLearned(wordId);
    // Increment daily progress when learning a new word
    updated = storageService.incrementDailyProgress();
    setUserProgressState(updated);
  };

  const showCelebration = () => {
    setGameState((prev) => ({
      ...prev,
      showCelebration: true,
    }));

    // Auto-hide after 3 seconds
    setTimeout(() => {
      hideCelebration();
    }, 3000);
  };

  const hideCelebration = () => {
    setGameState((prev) => ({
      ...prev,
      showCelebration: false,
    }));
  };

  const updateLevelProgress = (
    level: DifficultyLevel,
    completionPercentage: number,
    stars: number
  ) => {
    const updated = storageService.updateLevelProgress(level, {
      completionPercentage,
      stars,
    });
    setUserProgressState(updated);
  };

  const value: AppContextType = {
    userProfile,
    userProgress,
    gameState,
    setUserProfile,
    updateProgress,
    startGame,
    endGame,
    setCurrentWord,
    addScore,
    markWordLearned,
    showCelebration,
    hideCelebration,
    updateLevelProgress,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
